import React, { useState } from 'react';
import { 
  Card, 
  Tabs, 
  Form, 
  Input, 
  Button, 
  Checkbox, 
  message,
  Divider 
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined,
  GithubOutlined,
  GoogleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './index.css';

const { TabPane } = Tabs;

const Auth = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const navigate = useNavigate();

  const handleLogin = async (values) => {
    setLoginLoading(true);
    try {
      // 模拟登录API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockUser = {
        id: '1',
        username: values.username,
        email: `${values.username}@example.com`,
        token: 'mock-jwt-token',
        avatar: null
      };
      
      onLogin(mockUser);
      message.success('登录成功！');
      navigate('/');
    } catch (error) {
      message.error('登录失败，请重试');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setRegisterLoading(true);
    try {
      // 模拟注册API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('注册成功！请登录');
      setActiveTab('login');
      loginForm.setFieldsValue({
        username: values.username,
        password: values.password
      });
      registerForm.resetFields();
    } catch (error) {
      message.error('注册失败，请重试');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleThirdPartyAuth = (provider) => {
    message.info(`即将跳转到${provider}授权`);
    // 这里应该跳转到第三方授权页面
  };

  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">我的博客</h1>
          <p className="auth-subtitle">欢迎回来，请登录您的账户</p>
        </div>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          centered
          className="auth-tabs"
        >
          <TabPane tab="登录" key="login">
            <Form
              form={loginForm}
              onFinish={handleLogin}
              className="auth-form"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="用户名"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <div className="form-options">
                  <Checkbox>记住我</Checkbox>
                  <a href="/forgot-password" className="forgot-password">
                    忘记密码？
                  </a>
                </div>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loginLoading}
                  size="large"
                  block
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="注册" key="register">
            <Form
              form={registerForm}
              onFinish={handleRegister}
              className="auth-form"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { max: 20, message: '用户名最多20个字符' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="用户名"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="邮箱"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="确认密码"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error('请阅读并同意用户协议')),
                  },
                ]}
              >
                <Checkbox>
                  我已阅读并同意 <a href="/agreement">用户协议</a>
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={registerLoading}
                  size="large"
                  block
                >
                  注册
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>

        <Divider>或</Divider>

        <div className="third-party-auth">
          <Button
            icon={<GithubOutlined />}
            size="large"
            className="third-party-btn github"
            onClick={() => handleThirdPartyAuth('GitHub')}
          >
            GitHub 登录
          </Button>
          <Button
            icon={<GoogleOutlined />}
            size="large"
            className="third-party-btn google"
            onClick={() => handleThirdPartyAuth('Google')}
          >
            Google 登录
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;